import React, { useEffect, useState } from "react";

import axios from "axios";

import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";

import { currencyAmount } from "../../../helpers/TemplateHelper";

const formData = {
  id: 0,
  orderId: 0,
  userId: 0,
  type: null,
  measureUnit: null,
  unitPrice: null,
  value: "",
};

const ContributionDialog = ({
  visible,
  onCancel,
  orderId,
  toastrRef,
  onContributionAdded,
  contribution,
}) => {
  const [typeOptions, setTypeOptions] = useState([]);
  const [userIdOptions, setUserIdOptions] = useState([]);
  const [contributions, setContributions] = useState([{ ...formData }]);

  useEffect(() => {
    if (contribution) {
      setContributions([{ ...contribution }]);
    } else {
      setContributions([{ ...formData }]);
    }
  }, [visible, contribution]);

  useEffect(() => {
    axios
      .get("/api/ContributionTypes/enable")
      .then((response) => {
        const typeOptions = response.data.map((contributionType) => ({
          label: contributionType.type,
          value: contributionType.type,
          measureUnit: contributionType.measureUnit,
          unitPrice: contributionType.unitPrice,
        }));
        setTypeOptions(typeOptions);
      })
      .catch(() => {
        toastrRef.current.show({
          severity: "error",
          summary: "Contribution Type Error",
          detail:
            "An error occurred while fetching contribution types. Please try again.",
          life: 3000,
        });
      });

    axios
      .get("/api/User/active")
      .then((response) => {
        const userIdOptions = response.data.map((user) => ({
          label: `${user.id} - ${user.firstName} ${user.lastName}`,
          value: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
        }));
        setUserIdOptions(userIdOptions);
      })
      .catch(() => {
        toastrRef.current.show({
          severity: "error",
          summary: "Contribution User Error",
          detail:
            "An error occurred while fetching contribution user. Please try again.",
          life: 3000,
        });
      });
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Add orderId to all contributions
    const contributionsWithOrderId = contributions.map(contribution => ({
      ...contribution,
      orderId
    }));

    // Filter out any empty contributions
    const validContributions = contributionsWithOrderId.filter(
      contribution => contribution.userId && contribution.type && contribution.value
    );

    if (validContributions.length === 0) {
      toastrRef.current.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please add at least one valid contribution.",
        life: 3000,
      });
      return;
    }

    // Post all contributions
    Promise.all(
      validContributions.map(contribution =>
        axios.post("/api/Contributions", contribution)
      )
    )
      .then((responses) => {
        toastrRef.current.show({
          severity: "success",
          summary: "Contributions Added Successfully",
          detail: `${validContributions.length} contribution(s) have been successfully added.`,
          life: 3000,
        });
        onContributionAdded(responses[0].data);
        onCancel();
      })
      .catch(() => {
        toastrRef.current.show({
          severity: "error",
          summary: "Contribution Error",
          detail:
            "An error occurred while adding the contributions. Please try again.",
          life: 3000,
        });
      });
  };

  const handleContributionTypeChange = (e, index) => {
    const selectedType = typeOptions.find(
      (typeOption) => typeOption.value === e.value
    );
    const updatedContributions = [...contributions];
    updatedContributions[index] = {
      ...updatedContributions[index],
      type: e.value,
      measureUnit: selectedType.measureUnit,
      unitPrice: selectedType.unitPrice,
    };
    setContributions(updatedContributions);
  };

  const addContribution = () => {
    setContributions([...contributions, { ...formData }]);
  };

  const removeContribution = (index) => {
    const updatedContributions = contributions.filter((_, i) => i !== index);
    setContributions(updatedContributions);
  };

  const updateContribution = (index, field, value) => {
    const updatedContributions = [...contributions];
    updatedContributions[index] = {
      ...updatedContributions[index],
      [field]: value,
    };
    setContributions(updatedContributions);
  };

  return (
    <Dialog
      header="Add Contributions"
      visible={visible}
      onHide={onCancel}
      style={{ width: "900px", minHeight: "fit-content" }}
    >
      <form onSubmit={handleSubmit}>
        {contributions.map((contribution, index) => (
          <div key={index} className="p-field p-2">
            <div className="grid">
              <div className="col-12 md:col-4">
                <div className="p-field mb-2">
                  <label htmlFor={`userId-${index}`}>Employee *</label>
                  <Dropdown
                    id={`userId-${index}`}
                    options={userIdOptions}
                    value={contribution.userId}
                    onChange={(e) => updateContribution(index, "userId", e.value)}
                    placeholder="Select Employee"
                    required
                  />
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="p-field mb-2">
                  <label htmlFor={`contributionType-${index}`}>Contribution Type *</label>
                  <Dropdown
                    id={`contributionType-${index}`}
                    value={contribution.type}
                    options={typeOptions}
                    onChange={(e) => handleContributionTypeChange(e, index)}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select Contribution Type"
                    required
                  />
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="p-field mb-2">
                  <label htmlFor={`contributionValue-${index}`}>Contribution Value *</label>
                  <div className="flex align-items-center">
                    <InputText
                      id={`contributionValue-${index}`}
                      value={contribution.value}
                      type="number"
                      onChange={(e) => updateContribution(index, "value", e.target.value)}
                      required
                      autoComplete="off"
                      className="flex-grow-1"
                    />
                    {contributions.length > 1 && (
                      <Button
                        icon="pi pi-trash"
                        className="p-button-danger p-button-rounded p-button-text ml-2"
                        onClick={() => removeContribution(index)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            {contribution.type && (
              <div className="flex justify-content-between mt-1">
                <strong>Measure Unit: {contribution.measureUnit}</strong>
                <strong>Unit Price: {currencyAmount(contribution.unitPrice)} LKR </strong>
              </div>
            )}
          </div>
        ))}
        
        <div className="flex justify-content-between mb-3 ml-2">
          <Button
            type="button"
            label="Add Another Contribution"
            icon="pi pi-plus"
            className="p-button-info"
            onClick={addContribution}
          />
        </div>

        <div className="flex justify-content-end">
          <Button
            type="button"
            label="Cancel"
            className="p-button-text mr-2"
            onClick={onCancel}
          />
          <Button type="submit" label="Submit All" />
        </div>
      </form>
    </Dialog>
  );
};

export default ContributionDialog;
